// components/Reports/OrdersVsReturnsReport.jsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  ChevronDown,
  Calendar,
  Download,
  Filter,
  FileSpreadsheet,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Package,
  RefreshCw,
  SortAsc,
  Grid,
  Hash,
  AlertTriangle,
  Box,
  Layers,
  ArrowUpCircle,
  ArrowDownCircle,
  Percent,
  Target,
  Activity
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, branchesAPI, departmentAPI } from '../services/api';
import { toast } from 'react-toastify';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  format,
  parseISO,
  isSameDay,
  addHours,
} from 'date-fns';
import { arSA } from 'date-fns/locale';

// ==============================
// === INTERFACES ===
// ==============================
interface OrdersVsReturnsData {
  id: string;
  code: string;
  name: string;
  unit: string;
  department: string;
  branchSummary: { [branch: string]: { orders: number; returns: number; net?: number } };
  totalOrders: number;
  totalReturns: number;
  netQuantity: number;
  returnRate: number;
  efficiency?: number;
}

interface Branch {
  _id: string;
  name: string;
  nameEn?: string;
  displayName: string;
}

interface Department {
  _id: string;
  name: string;
  nameEn?: string;
}

interface QuickStats {
  totalReturns: number;
  totalOrders: number;
  totalNet: number;
  avgReturnRate: number;
  avgEfficiency?: number;
}

interface GroupByOption {
  value: string;
  labelAr: string;
  labelEn: string;
}

interface SortOption {
  value: string;
  labelAr: string;
  labelEn: string;
}

// ==============================
// === CONSTANTS ===
// ==============================
const PERIOD_OPTIONS = [
  { value: 'today', labelAr: 'اليوم', labelEn: 'Today' },
  { value: 'yesterday', labelAr: 'أمس', labelEn: 'Yesterday' },
  { value: 'week', labelAr: 'آخر 7 أيام', labelEn: 'Last 7 Days' },
  { value: 'month', labelAr: 'آخر 30 يوم', labelEn: 'Last 30 Days' },
  { value: 'custom', labelAr: 'فترة مخصصة', labelEn: 'Custom Range' },
];

const GROUP_BY_OPTIONS: GroupByOption[] = [
  { value: 'product', labelAr: 'حسب المنتج', labelEn: 'By Product' },
  { value: 'department', labelAr: 'حسب القسم', labelEn: 'By Department' },
];

const SORT_OPTIONS: SortOption[] = [
  { value: 'code_asc', labelAr: 'الكود (أ-ي)', labelEn: 'Code (A-Z)' },
  { value: 'code_desc', labelAr: 'الكود (ي-أ)', labelEn: 'Code (Z-A)' },
  { value: 'name_asc', labelAr: 'الاسم (أ-ي)', labelEn: 'Name (A-Z)' },
  { value: 'name_desc', labelAr: 'الاسم (ي-أ)', labelEn: 'Name (Z-A)' },
  { value: 'orders_desc', labelAr: 'الطلبات (الأكبر أولاً)', labelEn: 'Orders (High to Low)' },
  { value: 'returns_desc', labelAr: 'المرتجعات (الأكبر أولاً)', labelEn: 'Returns (High to Low)' },
  { value: 'returnRate_desc', labelAr: 'نسبة المرتجعات (الأعلى أولاً)', labelEn: 'Return Rate (High to Low)' },
  { value: 'efficiency_desc', labelAr: 'الكفاءة (الأعلى أولاً)', labelEn: 'Efficiency (High to Low)' },
  { value: 'net_desc', labelAr: 'الصافي (الأكبر أولاً)', labelEn: 'Net (High to Low)' },
];

// ==============================
// === UTILITY FUNCTIONS ===
// ==============================
const formatNumberWithComma = (num: number): string => {
  return num.toLocaleString('en-US');
};

const toArabicNumerals = (number: string | number): string => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return String(number).replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};

const formatNumber = (num: number, isRtl: boolean): string => {
  const formatted = formatNumberWithComma(num);
  return isRtl ? toArabicNumerals(formatted) : formatted;
};

const formatPercentage = (value: number, isRtl: boolean): string => {
  const formatted = value.toFixed(1);
  return isRtl ? toArabicNumerals(formatted) + '%' : formatted + '%';
};

const calculatePeriodDates = (period: string) => {
  const now = new Date();
  let start: Date, end: Date;

  switch (period) {
    case 'today':
      start = addHours(startOfDay(now), 12);
      end = endOfDay(now);
      break;
    case 'yesterday':
      const yesterday = subDays(now, 1);
      start = addHours(startOfDay(yesterday), 12);
      end = endOfDay(yesterday);
      break;
    case 'week':
      start = startOfDay(subDays(now, 6));
      end = endOfDay(now);
      break;
    case 'month':
      start = startOfDay(subMonths(now, 1));
      end = endOfDay(now);
      break;
    default:
      start = addHours(startOfDay(now), 12);
      end = endOfDay(now);
  }

  return { start, end };
};

const getReturnRateColor = (rate: number) => {
  if (rate <= 5) return 'text-green-600 bg-green-50 border-green-200';
  if (rate <= 15) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  if (rate <= 30) return 'text-orange-600 bg-orange-50 border-orange-200';
  return 'text-red-600 bg-red-50 border-red-200';
};

const getEfficiencyColor = (efficiency: number) => {
  if (efficiency >= 95) return 'text-green-600 bg-green-50 border-green-200';
  if (efficiency >= 85) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  if (efficiency >= 70) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  if (efficiency >= 50) return 'text-orange-600 bg-orange-50 border-orange-200';
  return 'text-red-600 bg-red-50 border-red-200';
};

const getNetColor = (net: number) => {
  if (net > 0) return 'text-green-600 bg-green-50 border-green-200';
  if (net < 0) return 'text-red-600 bg-red-50 border-red-200';
  return 'text-gray-600 bg-gray-50 border-gray-200';
};

const getQuantityCellColor = (quantity: number, type: 'orders' | 'returns') => {
  if (quantity === 0) return 'bg-gray-50';
  if (type === 'orders') {
    if (quantity >= 100) return 'bg-green-100';
    if (quantity >= 50) return 'bg-green-50';
    if (quantity >= 10) return 'bg-emerald-50';
    return 'bg-emerald-25';
  } else {
    if (quantity >= 50) return 'bg-red-100';
    if (quantity >= 20) return 'bg-red-50';
    if (quantity >= 5) return 'bg-rose-50';
    return 'bg-rose-25';
  }
};

// ==============================
// === SEARCH INPUT COMPONENT ===
// ==============================
const ProductSearchInput = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  ariaLabel: string;
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  
  return (
    <div className="relative w-full">
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${isRtl ? 'pl-10 pr-3' : 'pr-10 pl-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
        aria-label={ariaLabel}
      />
      {value ? (
        <button
          onClick={() => onChange({ target: { value: '' } } as any)}
          className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-600 transition-colors`}
          aria-label={isRtl ? 'مسح' : 'Clear'}
          type="button"
        >
          <X className="w-4 h-4" />
        </button>
      ) : (
        <Search className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
      )}
    </div>
  );
};

// ==============================
// === BUTTON COMPONENT ===
// ==============================
const Button: React.FC<{
  variant: 'primary' | 'outline' | 'success' | 'warning';
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ variant, onClick, disabled, children, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
      variant === 'primary'
        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow'
        : variant === 'success'
        ? 'bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow'
        : variant === 'warning'
        ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-sm hover:shadow'
        : 'border border-gray-300 hover:bg-gray-50 text-gray-700 hover:border-gray-400'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
    type="button"
  >
    {icon && <span className="flex-shrink-0">{icon}</span>}
    <span className="whitespace-nowrap">{children}</span>
  </button>
);

// ==============================
// === DROPDOWN COMPONENTS ===
// ==============================
const MultiSelectDropdown: React.FC<{
  value: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  label?: string;
}> = ({ value, onChange, options, ariaLabel, label }) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleOption = useCallback((optionValue: string) => {
    if (optionValue === 'all') {
      onChange(['all']); // ✅ إصلاح: عند اختيار "الكل" نعرض فقط "الكل"
    } else {
      // ✅ إصلاح: إزالة "all" عند اختيار قسم معين
      const newValues = value.filter(v => v !== 'all');
      if (newValues.includes(optionValue)) {
        // إزالة القسم إذا كان موجودًا
        const filtered = newValues.filter(v => v !== optionValue);
        onChange(filtered.length === 0 ? ['all'] : filtered); // إذا لم يبق شيء، نعود لـ "الكل"
      } else {
        // إضافة القسم الجديد
        onChange([...newValues, optionValue]);
      }
    }
  }, [value, onChange]);

  const allSelected = value.includes('all') || value.length === 0;
  const count = value.filter(v => v !== 'all').length;
  const displayLabel = allSelected
    ? isRtl ? 'جميع الأقسام' : 'All Departments'
    : isRtl
      ? `${count} ${count === 1 ? 'قسم' : 'أقسام'}`
      : `${count} ${count === 1 ? 'Department' : 'Departments'}`;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          dir={isRtl ? 'rtl' : 'ltr'}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm text-sm font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
          aria-label={ariaLabel}
          type="button"
        >
          <span className="truncate flex-1">{displayLabel}</span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
          >
            {options.map(opt => {
              const isSelected = allSelected ? opt.value === 'all' : value.includes(opt.value);
              return (
                <div 
                  key={opt.value} 
                  className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                  onClick={() => toggleOption(opt.value)}
                >
                  <span className={`text-sm flex-1 ${isSelected ? 'text-blue-600 font-semibold' : 'text-gray-700'}`}>
                    {opt.label}
                  </span>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className={`h-4 w-4 rounded border-gray-300 focus:ring-blue-500 cursor-pointer ${
                      isSelected ? 'text-blue-600' : 'text-gray-300'
                    }`}
                    readOnly
                  />
                </div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

const Dropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  label?: string;
  icon?: React.ReactNode;
}> = ({ value, onChange, options, ariaLabel, label, icon }) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          dir={isRtl ? 'rtl' : 'ltr'}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm text-sm font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
          aria-label={ariaLabel}
          type="button"
        >
          <div className="flex items-center gap-1.5 flex-1 truncate">
            {icon && <span className="flex-shrink-0">{icon}</span>}
            <span className="truncate">{selectedOption?.label || ''}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
          >
            {options.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-sm ${isRtl ? 'text-right' : 'text-left'} ${
                  value === opt.value 
                    ? 'bg-blue-50 text-blue-700 font-semibold' 
                    : 'text-gray-700 hover:bg-gray-50'
                } transition-colors border-b border-gray-100 last:border-b-0`}
                type="button"
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ==============================
// === STATS COMPONENTS ===
// ==============================
const StatsCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'emerald';
  loading?: boolean;
  trend?: string;
}> = ({ title, value, icon, color, loading, trend }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
  };

  return (
    <div className={`p-4 rounded-xl border ${colors[color]} transition-all hover:shadow-sm`}>
      <div className="flex items-center justify-between mb-2">
        <div className="p-2 rounded-lg bg-white">
          {icon}
        </div>
        {trend && (
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-white/80">
            {trend}
          </span>
        )}
      </div>
      <div className="mb-1">
        <h3 className="text-sm font-medium opacity-80">{title}</h3>
      </div>
      <div className="text-xl font-bold">
        {loading ? (
          <div className="h-6 w-20 bg-gray-200 rounded animate-pulse"></div>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

// ==============================
// === EXCEL EXPORT ===
// ==============================
const exportToExcel = async (
  data: OrdersVsReturnsData[],
  periodLabel: string,
  departmentsLabel: string,
  allBranches: string[],
  isRtl: boolean,
  groupBy: string,
  sortBy: string,
  totals: any
) => {
  const toastId = 'excel-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف Excel...' : 'Generating Excel...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(isRtl ? 'مقارنة_الطلبات_والمرتجعات' : 'Orders_vs_Returns');

    const headerBgColor = { argb: 'FF3B82F6' };
    const totalRowColor = { argb: 'FFF59E0B' };
    const greenCellColor = { argb: 'FFDCFCE7' };
    const lightGreenCellColor = { argb: 'FFECFDF5' };
    const redCellColor = { argb: 'FFFEE2E2' };
    const lightRedCellColor = { argb: 'FFFEF2F2' };
    const altRowColor = { argb: 'FFF9FAFB' };
    const borderColor = { argb: 'FF4B5563' };

    // إضافة معلومات التقرير
    const infoRow = worksheet.addRow([]);
    infoRow.height = 25;
    
    // الفترة
    const periodCell = infoRow.getCell(1);
    periodCell.value = periodLabel;
    periodCell.font = { size: 11, color: { argb: 'FF6B7280' }, bold: true };
    periodCell.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // التجميع
    const groupByOption = GROUP_BY_OPTIONS.find(g => g.value === groupBy);
    const groupByLabel = isRtl 
      ? `التجميع: ${groupByOption?.labelAr || ''}`
      : `Group by: ${groupByOption?.labelEn || ''}`;
    const groupCell = infoRow.getCell(2);
    groupCell.value = groupByLabel;
    groupCell.font = { size: 11, color: { argb: 'FF6B7280' }, bold: true };
    groupCell.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // الترتيب
    const sortByOption = SORT_OPTIONS.find(s => s.value === sortBy);
    const sortByLabel = isRtl 
      ? `الترتيب: ${sortByOption?.labelAr || ''}`
      : `Sort by: ${sortByOption?.labelEn || ''}`;
    const sortCell = infoRow.getCell(3);
    sortCell.value = sortByLabel;
    sortCell.font = { size: 11, color: { argb: 'FF6B7280' }, bold: true };
    sortCell.alignment = { horizontal: 'center', vertical: 'middle' };
    
    // الأقسام
    if (departmentsLabel) {
      const deptText = isRtl ? `الأقسام: ${departmentsLabel}` : `Departments: ${departmentsLabel}`;
      const deptCell = infoRow.getCell(4);
      deptCell.value = deptText;
      deptCell.font = { size: 11, color: { argb: 'FF6B7280' }, bold: true };
      deptCell.alignment = { horizontal: 'center', vertical: 'middle' };
    }

    // تفعيل الاتجاه من اليمين لليسار للعربية
    if (isRtl) {
      worksheet.views = [{ rightToLeft: true }];
    }

    // إعداد العناوين
    const headers = isRtl
      ? [
          'الكود',
          'اسم المنتج',
          ...allBranches.flatMap(branch => [`طلبات ${branch}`, `مرتجعات ${branch}`]),
          'إجمالي الطلبات',
          'إجمالي المرتجعات',
          'الصافي',
          'نسبة المرتجعات %',
          'الكفاءة %'
        ]
      : [
          'Code',
          'Product Name',
          ...allBranches.flatMap(branch => [`Orders ${branch}`, `Returns ${branch}`]),
          'Total Orders',
          'Total Returns',
          'Net',
          'Return Rate %',
          'Efficiency %'
        ];

    // إضافة العناوين
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Arial' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: headerBgColor };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: borderColor },
        left: { style: 'thin', color: borderColor },
        bottom: { style: 'thin', color: borderColor },
        right: { style: 'thin', color: borderColor },
      };
    });

    // إضافة البيانات
    data.forEach((item, idx) => {
      const efficiency = item.efficiency || (100 - item.returnRate);
      const rowData = [
        item.code || '-',
        item.name,
        ...allBranches.flatMap(branch => [
          item.branchSummary?.[branch]?.orders || 0,
          item.branchSummary?.[branch]?.returns || 0
        ]),
        item.totalOrders,
        item.totalReturns,
        item.netQuantity,
        item.returnRate,
        efficiency
      ];
      
      const excelRow = worksheet.addRow(rowData);
      
      // توسيط جميع الخلايا
      excelRow.eachCell((cell, colNumber) => {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.font = { size: 10, name: 'Arial' };
        
        // تنسيق الأرقام
        if (colNumber > 2) {
          if (typeof cell.value === 'number') {
            // أعمدة الطلبات والمرتجعات
            if (colNumber <= 2 + (allBranches.length * 2)) {
              cell.numFmt = '#,##0';
              if (isRtl) {
                cell.value = toArabicNumerals(cell.value.toString());
              }
            } 
            // نسبة المرتجعات والكفاءة
            else if (colNumber === headers.length - 1 || colNumber === headers.length) {
              cell.numFmt = '0.0%';
              if (isRtl) {
                cell.value = toArabicNumerals((cell.value as number / 100).toFixed(3));
              } else {
                cell.value = cell.value as number / 100;
              }
            }
            // الصافي
            else if (colNumber === headers.length - 2) {
              cell.numFmt = '#,##0';
              if (isRtl) {
                cell.value = toArabicNumerals(cell.value.toString());
              }
            }
          }
        }
      });
      
      // تلوين الصفوف الفردية
      if (idx % 2 === 0) {
        excelRow.eachCell((cell) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: altRowColor };
        });
      }
      
      // تلوين خلايا الطلبات باللون الأخضر
      for (let i = 3; i <= 2 + (allBranches.length * 2); i += 2) {
        const ordersCell = excelRow.getCell(i);
        const ordersValue = ordersCell.value as number;
        if (ordersValue > 0) {
          ordersCell.fill = ordersValue >= 50 ? 
            { type: 'pattern', pattern: 'solid', fgColor: greenCellColor } :
            { type: 'pattern', pattern: 'solid', fgColor: lightGreenCellColor };
          ordersCell.font = { color: { argb: 'FF166534' } };
        }
      }
      
      // تلوين خلايا المرتجعات باللون الأحمر
      for (let i = 4; i <= 2 + (allBranches.length * 2); i += 2) {
        const returnsCell = excelRow.getCell(i);
        const returnsValue = returnsCell.value as number;
        if (returnsValue > 0) {
          returnsCell.fill = returnsValue >= 20 ? 
            { type: 'pattern', pattern: 'solid', fgColor: redCellColor } :
            { type: 'pattern', pattern: 'solid', fgColor: lightRedCellColor };
          returnsCell.font = { color: { argb: 'FFDC2626' } };
        }
      }
      
      // تلوين خلية الصافي
      const netCell = excelRow.getCell(headers.length - 2);
      if (item.netQuantity > 0) {
        netCell.fill = { type: 'pattern', pattern: 'solid', fgColor: greenCellColor };
        netCell.font = { color: { argb: 'FF166534' } };
      } else if (item.netQuantity < 0) {
        netCell.fill = { type: 'pattern', pattern: 'solid', fgColor: redCellColor };
        netCell.font = { color: { argb: 'FFDC2626' } };
      }
      
      // تلوين خلية نسبة المرتجعات
      const rateCell = excelRow.getCell(headers.length - 1);
      if (item.returnRate <= 5) {
        rateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: greenCellColor };
        rateCell.font = { color: { argb: 'FF166534' } };
      } else if (item.returnRate <= 15) {
        rateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF9C3' } };
        rateCell.font = { color: { argb: 'FFCA8A04' } };
      } else if (item.returnRate <= 30) {
        rateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFED7AA' } };
        rateCell.font = { color: { argb: 'FFEA580C' } };
      } else {
        rateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: redCellColor };
        rateCell.font = { color: { argb: 'FFDC2626' } };
      }
      
      // تلوين خلية الكفاءة
      const efficiencyCell = excelRow.getCell(headers.length);
      if (efficiency >= 95) {
        efficiencyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: greenCellColor };
        efficiencyCell.font = { color: { argb: 'FF166534' } };
      } else if (efficiency >= 85) {
        efficiencyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } };
        efficiencyCell.font = { color: { argb: 'FF059669' } };
      } else if (efficiency >= 70) {
        efficiencyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF9C3' } };
        efficiencyCell.font = { color: { argb: 'FFCA8A04' } };
      } else if (efficiency >= 50) {
        efficiencyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFED7AA' } };
        efficiencyCell.font = { color: { argb: 'FFEA580C' } };
      } else {
        efficiencyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: redCellColor };
        efficiencyCell.font = { color: { argb: 'FFDC2626' } };
      }
    });

    // إضافة صف الإجماليات
    const totalRowData = [
      isRtl ? 'الإجمالي' : 'Total',
      '',
      ...allBranches.flatMap(branch => [
        data.reduce((sum, item) => sum + (item.branchSummary?.[branch]?.orders || 0), 0),
        data.reduce((sum, item) => sum + (item.branchSummary?.[branch]?.returns || 0), 0)
      ]),
      totals?.totalOrders || 0,
      totals?.totalReturns || 0,
      totals?.totalNet || 0,
      totals?.avgReturnRate || 0,
      totals?.avgEfficiency || (100 - (totals?.avgReturnRate || 0))
    ];
    
    const totalRow = worksheet.addRow(totalRowData);
    totalRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Arial' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: totalRowColor };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: borderColor },
        left: { style: 'thin', color: borderColor },
        bottom: { style: 'thin', color: borderColor },
        right: { style: 'thin', color: borderColor },
      };
      
      // تنسيق الأرقام في صف الإجماليات
      if (colNumber > 2 && colNumber <= 2 + (allBranches.length * 2)) {
        cell.numFmt = '#,##0';
        if (isRtl && typeof cell.value === 'number') {
          cell.value = toArabicNumerals(cell.value.toString());
        }
      } else if (colNumber === headers.length - 1 || colNumber === headers.length) {
        // نسبة المرتجعات والكفاءة
        cell.numFmt = '0.0%';
        if (isRtl && typeof cell.value === 'number') {
          cell.value = toArabicNumerals((cell.value / 100).toFixed(3));
        } else if (typeof cell.value === 'number') {
          cell.value = cell.value / 100;
        }
      } else if (colNumber === headers.length - 2) {
        // الصافي
        cell.numFmt = '#,##0';
        if (isRtl && typeof cell.value === 'number') {
          cell.value = toArabicNumerals(cell.value.toString());
        }
      }
    });

    // ضبط عرض الأعمدة
    worksheet.columns.forEach((column, colNumber) => {
      let maxLength = 10;
      if (column.eachCell) {
        column.eachCell({ includeEmpty: true }, (cell) => {
          const length = cell.value ? cell.value.toString().length : 0;
          if (length > maxLength) maxLength = length;
        });
      }
      
      // تحديد عرض مناسب حسب نوع العمود
      if (colNumber === 1) { // الكود
        column.width = 15;
      } else if (colNumber === 2) { // اسم المنتج
        column.width = 30;
      } else if (colNumber <= 2 + (allBranches.length * 2)) { // أعمدة الفروع
        column.width = 15;
      } else { // الأعمدة الإجمالية
        column.width = 18;
      }
    });

    // حفظ الملف
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const fileName = `orders_vs_returns_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى Excel بنجاح!' : 'Exported to Excel successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Excel Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير Excel. حاول مرة أخرى.' : 'Excel export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === MAIN COMPONENT ===
// ==============================
const OrdersVsReturnsReport: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRtl = language === 'ar';
  
  // States
  const [reportData, setReportData] = useState<OrdersVsReturnsData[]>([]);
  const [quickStats, setQuickStats] = useState<QuickStats | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [groupBy, setGroupBy] = useState('product');
  const [sortBy, setSortBy] = useState('returnRate_desc');
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [totals, setTotals] = useState<any>({});
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Memoized values
  const periodOptions = useMemo(
    () => PERIOD_OPTIONS.map(p => ({
      value: p.value,
      label: isRtl ? p.labelAr : p.labelEn
    })),
    [isRtl]
  );

  const groupByOptions = useMemo(
    () => GROUP_BY_OPTIONS.map(g => ({
      value: g.value,
      label: isRtl ? g.labelAr : g.labelEn
    })),
    [isRtl]
  );

  const sortOptions = useMemo(
    () => SORT_OPTIONS.map(s => ({
      value: s.value,
      label: isRtl ? s.labelAr : s.labelEn
    })),
    [isRtl]
  );

  const departmentOptions = useMemo(() => {
    const baseOptions = departments.map(d => ({
      value: d._id,
      label: isRtl ? d.name : (d.nameEn || d.name),
    })).sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 'all', label: isRtl ? 'جميع الأقسام' : 'All Departments' }, ...baseOptions];
  }, [departments, isRtl]);

  // Enhanced search function
  const searchRowData = useCallback((item: OrdersVsReturnsData, term: string): boolean => {
    if (!term.trim()) return true;
    
    const searchLower = term.toLowerCase();
    
    if (item.code?.toLowerCase().includes(searchLower)) return true;
    if (item.name.toLowerCase().includes(searchLower)) return true;
    if (item.department?.toLowerCase().includes(searchLower)) return true;
    
    const hasBranchMatch = Object.entries(item.branchSummary || {}).some(([branch, data]) => {
      const branchName = branches.find(b => b.displayName === branch)?.displayName || branch;
      return branchName.toLowerCase().includes(searchLower) || 
             data.orders.toString().includes(searchLower) ||
             data.returns.toString().includes(searchLower);
    });
    
    if (hasBranchMatch) return true;
    
    return false;
  }, [branches]);

  // Fetch data functions
  const fetchBranches = useCallback(async () => {
    try {
      const res = await branchesAPI.getAll();
      const branchesData = Array.isArray(res) 
        ? res.map(b => ({
            _id: b._id,
            name: b.name,
            nameEn: b.nameEn,
            displayName: isRtl ? b.name : (b.nameEn || b.name),
          }))
        : (res.data || []).map((b: any) => ({
            _id: b._id,
            name: b.name,
            nameEn: b.nameEn,
            displayName: isRtl ? b.name : (b.nameEn || b.name),
          }));
      setBranches(branchesData);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  }, [isRtl]);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await departmentAPI.getAll({ limit: 100 });
      const departmentsData = Array.isArray(res) ? res : (res.data || []);
      setDepartments(departmentsData);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    if (!user || (user.role !== 'admin' && user.role !== 'production')) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setStatsLoading(true);
    try {
      const { start, end } = calculatePeriodDates(period);
      const currentStartDate = startDate || start.toISOString().split('T')[0];
      const currentEndDate = endDate || end.toISOString().split('T')[0];

      const params: any = {
        startDate: currentStartDate,
        endDate: currentEndDate,
        groupBy: groupBy,
        lang: language,
        isRtl: isRtl.toString(),
      };

      // ✅ إصلاح: إرسال الأقسام المختارة بشكل صحيح
      if (!selectedDepartments.includes('all') && selectedDepartments.length > 0) {
        params.department = selectedDepartments.join(',');
      }

      console.log('📤 إرسال طلب API:', params);
      
      const response = await reportsAPI.getOrdersVsReturns(params);
      
      if (response.success) {
        const data = response.data || [];
        const stats = response.totals || {};
        
        // معالجة البيانات مع إضافة الكفاءة
        const processedData = data.map((item: any) => {
          // حساب الصافي (الطلبات - المرتجعات)
          const net = (item.totalOrders || 0) - (item.totalReturns || 0);
          // حساب نسبة المرتجعات
          const returnRate = item.totalOrders > 0 
            ? ((item.totalReturns || 0) / item.totalOrders) * 100 
            : 0;
          // حساب الكفاءة
          const efficiency = 100 - returnRate;

          // ✅ تحسين branchSummary لحساب الصافي لكل فرع
          const branchSummary = item.branchSummary || {};
          Object.keys(branchSummary).forEach(branch => {
            const branchData = branchSummary[branch];
            branchData.net = (branchData.orders || 0) - (branchData.returns || 0);
          });

          return {
            ...item,
            id: item.id || item._id || Math.random().toString(),
            code: item.code || 'N/A',
            name: isRtl ? (item.name || item.displayName) : (item.nameEn || item.name || item.displayName),
            department: isRtl ? (item.department || 'عام') : (item.department || 'General'),
            unit: item.unit || (isRtl ? 'قطعة' : 'Piece'),
            netQuantity: net,
            returnRate: returnRate,
            efficiency: efficiency,
            branchSummary
          };
        }).filter(item => item.totalOrders > 0 || item.totalReturns > 0);
        
        setReportData(processedData);
        setTotals(stats);
        
        // تحديث الإحصائيات السريعة
        const totalReturns = processedData.reduce((sum, item) => sum + item.totalReturns, 0);
        const totalOrders = processedData.reduce((sum, item) => sum + item.totalOrders, 0);
        const totalNet = processedData.reduce((sum, item) => sum + item.netQuantity, 0);
        const avgReturnRate = totalOrders > 0 ? (totalReturns / totalOrders) * 100 : 0;
        const avgEfficiency = 100 - avgReturnRate;
        
        setQuickStats({
          totalReturns,
          totalOrders,
          totalNet,
          avgReturnRate,
          avgEfficiency
        });
        
        setLastRefresh(new Date());
        
        // تحديث قائمة الفروع من البيانات
        if (processedData.length > 0) {
          const allBranchNames = new Set<string>();
          processedData.forEach(item => {
            Object.keys(item.branchSummary || {}).forEach(branch => {
              allBranchNames.add(branch);
            });
          });
          
          const branchesFromData = Array.from(allBranchNames).map((branchName, index) => ({
            _id: `branch-${index}`,
            name: branchName,
            displayName: branchName
          }));
          
          setBranches(prev => {
            const existingNames = new Set(prev.map(b => b.displayName));
            const newBranches = branchesFromData.filter(b => !existingNames.has(b.displayName));
            return [...prev, ...newBranches];
          });
        }
      } else {
        toast.error(response.message || (isRtl ? 'فشل جلب التقرير' : 'Failed to load report'));
      }
    } catch (error: any) {
      console.error('Error fetching orders vs returns report:', error);
      toast.error(isRtl ? 'فشل جبل التقرير' : 'Failed to load report');
      setReportData([]);
      setTotals({});
      setQuickStats(null);
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  }, [user, period, startDate, endDate, groupBy, selectedDepartments, isRtl, language]);

  // Initial data fetch
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'production')) {
      Promise.all([
        fetchBranches(),
        fetchDepartments(),
        fetchReport()
      ]);
    }
  }, [user, fetchBranches, fetchDepartments, fetchReport]);

  // Fetch data when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (user && (user.role === 'admin' || user.role === 'production')) {
        fetchReport();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [period, groupBy, selectedDepartments, user, fetchReport]);

  // Filter and sort data
  const filteredData = useMemo(() => {
    let data = [...reportData];
    
    // ✅ تطبيق فلتر الأقسام
    if (!selectedDepartments.includes('all') && selectedDepartments.length > 0) {
      data = data.filter(item => 
        selectedDepartments.some(deptId => 
          item.department?.toLowerCase().includes(
            departments.find(d => d._id === deptId)?.name?.toLowerCase() || ''
          )
        )
      );
    }
    
    // تطبيق البحث
    if (searchTerm.trim()) {
      data = data.filter(item => searchRowData(item, searchTerm));
    }
    
    // الترتيب
    data.sort((a, b) => {
      switch (sortBy) {
        case 'code_asc':
          return (a.code || '').localeCompare(b.code || '');
        case 'code_desc':
          return (b.code || '').localeCompare(a.code || '');
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'orders_desc':
          return b.totalOrders - a.totalOrders;
        case 'returns_desc':
          return b.totalReturns - a.totalReturns;
        case 'returnRate_desc':
          return (b.returnRate || 0) - (a.returnRate || 0);
        case 'efficiency_desc':
          return (b.efficiency || 0) - (a.efficiency || 0);
        case 'net_desc':
          return (b.netQuantity || 0) - (a.netQuantity || 0);
        default:
          return (b.returnRate || 0) - (a.returnRate || 0);
      }
    });
    
    return data;
  }, [reportData, searchTerm, sortBy, searchRowData, selectedDepartments, departments]);

  // Generate labels
  const periodLabel = useMemo(() => {
    if (period === 'custom') {
      if (startDate && endDate && isSameDay(parseISO(startDate), parseISO(endDate))) {
        return isRtl 
          ? `يوم واحد - ${format(parseISO(startDate), 'EEEE d/M/yyyy', { locale: arSA })}`
          : `Single Day - ${format(parseISO(startDate), 'EEEE d/M/yyyy')}`;
      }
      return isRtl
        ? `${format(parseISO(startDate || new Date().toISOString()), 'd/M/yyyy', { locale: arSA })} - ${format(parseISO(endDate || new Date().toISOString()), 'd/M/yyyy', { locale: arSA })}`
        : `${format(parseISO(startDate || new Date().toISOString()), 'd/M/yyyy')} - ${format(parseISO(endDate || new Date().toISOString()), 'd/M/yyyy')}`;
    }
    
    const { start, end } = calculatePeriodDates(period);
    if (isSameDay(start, end)) {
      return isRtl
        ? `${format(start, 'EEEE d/M/yyyy', { locale: arSA })}`
        : `${format(start, 'EEEE d/M/yyyy')}`;
    }
    return isRtl
      ? `${format(start, 'd/M/yyyy', { locale: arSA })} - ${format(end, 'd/M/yyyy', { locale: arSA })}`
      : `${format(start, 'd/M/yyyy')} - ${format(end, 'd/M/yyyy')}`;
  }, [period, startDate, endDate, isRtl]);

  const departmentsLabel = useMemo(() => {
    if (selectedDepartments.includes('all')) return isRtl ? 'جميع الأقسام' : 'All Departments';
    const selectedNames = selectedDepartments
      .map(id => departments.find(d => d._id === id)?.name || '')
      .filter(Boolean);
    return selectedNames.join('، ');
  }, [selectedDepartments, departments, isRtl]);

  const allBranches = useMemo(() => 
    Array.from(new Set(branches.map(b => b.displayName))).sort(),
    [branches]
  );

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setPeriod('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setPeriod('custom');
  };

  const handleRefresh = () => {
    fetchReport();
    toast.success(isRtl ? 'تم تحديث البيانات' : 'Data refreshed successfully');
  };

  const handleExportExcel = () => {
    exportToExcel(
      filteredData,
      periodLabel,
      departmentsLabel,
      allBranches,
      isRtl,
      groupBy,
      sortBy,
      {
        totalOrders: quickStats?.totalOrders || 0,
        totalReturns: quickStats?.totalReturns || 0,
        totalNet: quickStats?.totalNet || 0,
        avgReturnRate: quickStats?.avgReturnRate || 0,
        avgEfficiency: quickStats?.avgEfficiency || 0
      }
    );
  };

  if (!user || (user.role !== 'admin' && user.role !== 'production')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
            <X className="w-4 h-4 text-red-600" />
          </div>
          <span className="text-sm text-red-600">{isRtl ? 'غير مصرح لك للوصول إلى هذه الصفحة' : 'Not authorized to access this page'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 mx-auto">
      {/* Header */}
      <div className="w-full mb-6">
        <div className="flex flex-col md:flex-row justify-between items-center  md:items-start  gap-6 mb-6 text-center md:text-left">
          <div className="flex flex-col md:flex-row items-center mt-3 md:mt-0 gap-3 w-full md:w-auto ">
            <div className="p-2.5 bg-blue-100 rounded-xl hidden md:flex">
              <BarChart3 className="w-5 h-5 text-blue-700" />
            </div>
            <div className={isRtl ? 'text-right' : 'text-left'}>
              <h1 className="text-xl font-bold md:text-right text-center text-gray-800">
                {isRtl ? 'مقارنة الطلبات والمرتجعات' : 'Orders vs Returns Comparison'}
              </h1>
              <p className="text-sm md:text-right text-center text-gray-500 mt-1">
                {isRtl ? 'تحليل أداء المنتجات ومقارنة الطلبات مع المرتجعات' : 'Product performance analysis and orders vs returns comparison'}
              </p>
            </div>
          </div>
          
          {/* الأزرار */}
          <div className={`flex items-center gap-2 justify-center md:justify-start ${isRtl ? 'flex-row-reverse' : ''} w-full md:w-auto`}>
            <Button
              variant="outline"
              onClick={handleRefresh}
              icon={<RefreshCw className="w-4 h-4" />}
            >
              {isRtl ? 'تحديث' : 'Refresh'}
            </Button>
            
            <button
              className="text-sm text-white py-2 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 hover:underline   "
              onClick={handleExportExcel}
              disabled={filteredData.length === 0}
              icon={<Download className="w-4 h-4" />}
            >
              {isRtl ? 'تصدير Excel' : 'Export Excel'}
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatsCard
            title={isRtl ? 'إجمالي المرتجعات' : 'Total Returns'}
            value={statsLoading ? '...' : formatNumber(quickStats?.totalReturns || 0, isRtl)}
            icon={<TrendingDown className="w-4 h-4" />}
            color="red"
            loading={statsLoading}
          />
          <StatsCard
            title={isRtl ? 'إجمالي الطلبات' : 'Total Orders'}
            value={statsLoading ? '...' : formatNumber(quickStats?.totalOrders || 0, isRtl)}
            icon={<TrendingUp className="w-4 h-4" />}
            color="green"
            loading={statsLoading}
          />
          <StatsCard
            title={isRtl ? 'إجمالي الصافي' : 'Total Net'}
            value={statsLoading ? '...' : formatNumber(quickStats?.totalNet || 0, isRtl)}
            icon={<Box className="w-4 h-4" />}
            color="blue"
            loading={statsLoading}
          />
          <StatsCard
            title={isRtl ? 'متوسط نسبة المرتجعات' : 'Avg Return Rate'}
            value={statsLoading ? '...' : formatPercentage(quickStats?.avgReturnRate || 0, isRtl)}
            icon={<Percent className="w-4 h-4" />}
            color="amber"
            loading={statsLoading}
          />
       
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          {/* البحث و3 dropdowns في صف واحد */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
            {/* البحث */}
            <div className="lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRtl ? 'بحث متقدم' : 'Advanced Search'}
              </label>
              <ProductSearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={isRtl ? 'ابحث في أي حقل من الجدول...' : 'Search across all table fields...'}
                ariaLabel={isRtl ? 'بحث متقدم' : 'Advanced search'}
              />
            </div>

            {/* الفترة */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {isRtl ? 'الفترة' : 'Period'}
              </label>
              <Dropdown
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                ariaLabel={isRtl ? 'اختر الفترة' : 'Select period'}
                icon={<Calendar className="w-3 h-3" />}
              />
            </div>
          </div>

          {/* 3 dropdowns في صف واحد */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {/* الأقسام */}
            <MultiSelectDropdown
              value={selectedDepartments}
              onChange={setSelectedDepartments}
              options={departmentOptions}
              ariaLabel={isRtl ? 'اختر الأقسام' : 'Select departments'}
              label={isRtl ? 'الأقسام' : 'Departments'}
            />

            {/* التجميع */}
            <Dropdown
              value={groupBy}
              onChange={setGroupBy}
              options={groupByOptions}
              ariaLabel={isRtl ? 'اختر طريقة التجميع' : 'Select grouping method'}
              label={isRtl ? 'التجميع' : 'Group By'}
              icon={<Grid className="w-3 h-3" />}
            />

            {/* الترتيب */}
            <Dropdown
              value={sortBy}
              onChange={setSortBy}
              options={sortOptions}
              ariaLabel={isRtl ? 'اختر طريقة الترتيب' : 'Select sorting method'}
              label={isRtl ? 'الترتيب' : 'Sort By'}
              icon={<SortAsc className="w-3 h-3" />}
            />
          </div>

          {/* التواريخ المخصصة */}
          {period === 'custom' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {isRtl ? 'من تاريخ' : 'From Date'}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  className="w-full py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {isRtl ? 'إلى تاريخ' : 'To Date'}
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  className="w-full py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  dir="ltr"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Report Header Info */}
      {!loading && filteredData.length > 0 && (
        <div className="mb-4 p-4 bg-white rounded-xl border border-gray-200">
          <div className={`flex flex-wrap items-center justify-center md:justify-start gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg min-w-[150px]">
              <Calendar className="w-3 h-3 text-gray-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 truncate">{periodLabel}</span>
            </div>
            
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg min-w-[150px]">
              <Grid className="w-3 h-3 text-gray-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 truncate">
                {isRtl ? `التجميع: ${groupByOptions.find(g => g.value === groupBy)?.labelAr || ''}` : 
                       `Group by: ${groupByOptions.find(g => g.value === groupBy)?.labelEn || ''}`}
              </span>
            </div>
            
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg min-w-[150px]">
              <SortAsc className="w-3 h-3 text-gray-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 truncate">
                {isRtl ? `الترتيب: ${sortOptions.find(s => s.value === sortBy)?.labelAr || ''}` : 
                       `Sort by: ${sortOptions.find(s => s.value === sortBy)?.labelEn || ''}`}
              </span>
            </div>
            
            {departmentsLabel && (
              <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg min-w-[150px]">
                <Filter className="w-3 h-3 text-gray-500 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 truncate">
                  {isRtl ? `الأقسام: ${departmentsLabel}` : `Departments: ${departmentsLabel}`}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">{isRtl ? 'جارٍ تحميل البيانات...' : 'Loading data...'}</p>
          </div>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="inline-block p-3 bg-gray-100 rounded-full mb-3">
            <Filter className="w-6 h-6 text-gray-500" />
          </div>
          <h3 className="text-base font-semibold text-gray-700 mb-2">{isRtl ? 'لا توجد بيانات' : 'No data found'}</h3>
          <p className="text-gray-600 text-sm">
            {isRtl 
              ? 'لا توجد بيانات مطابقة لبحثك أو الفلاتر المحددة.'
              : 'No data matches your search or selected filters.'
            }
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" dir={isRtl ? 'rtl' : 'ltr'}>
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-700">
                  {isRtl ? 'تفاصيل التقرير' : 'Report Details'}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {isRtl ? 'آخر تحديث:' : 'Last updated:'} {format(lastRefresh, 'HH:mm')}
                </p>
              </div>
              <div className="text-xs text-gray-600 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                {formatNumber(filteredData.length, isRtl)} {isRtl ? 'منتج' : 'products'}
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full w-full text-xs border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  {/* الأعمدة الأساسية */}
                  <th className={`px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap border-r border-gray-200 min-w-[80px] ${isRtl ? 'border-l' : 'border-r'}`}>
                    <div className="flex items-center justify-center gap-1">
                      <Hash className="w-3 h-3" />
                      {isRtl ? 'الكود' : 'Code'}
                    </div>
                  </th>
                  <th className={`px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap border-r border-gray-200 min-w-[150px] ${isRtl ? 'border-l' : 'border-r'}`}>
                    {isRtl ? 'اسم المنتج' : 'Product Name'}
                  </th>
                  
                  {/* أعمدة الفروع - لكل فرع عمودين: طلبات + مرتجعات */}
                  {allBranches.map((branch, branchIndex) => (
                    <React.Fragment key={branch}>
                      <th 
                        className={`px-2 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap border-r border-gray-200 min-w-[60px] ${branchIndex === allBranches.length - 1 && isRtl ? 'border-l-0' : ''}`}
                        colSpan={2}
                      >
                        <div className="flex flex-col items-center">
                          <span className="text-[10px] font-medium truncate max-w-[50px]">{branch}</span>
                          <div className="flex gap-1 mt-1">
                            <span className="text-[9px] text-green-600">{isRtl ? 'طلبات' : 'Orders'}</span>
                            <span className="text-[9px] text-red-600">{isRtl ? 'مرتجعات' : 'Returns'}</span>
                          </div>
                        </div>
                      </th>
                    </React.Fragment>
                  ))}
                  
                  {/* الأعمدة الإجمالية */}
                  <th className={`px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap border-r border-gray-200 min-w-[80px] ${isRtl ? 'border-l' : 'border-r'}`}>
                    {isRtl ? 'إجمالي الطلبات' : 'Total Orders'}
                  </th>
                  <th className={`px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap border-r border-gray-200 min-w-[80px] ${isRtl ? 'border-l' : 'border-r'}`}>
                    {isRtl ? 'إجمالي المرتجعات' : 'Total Returns'}
                  </th>
                  <th className={`px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap border-r border-gray-200 min-w-[80px] ${isRtl ? 'border-l' : 'border-r'}`}>
                    {isRtl ? 'الصافي' : 'Net'}
                  </th>
                  <th className={`px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap border-r border-gray-200 min-w-[90px] ${isRtl ? 'border-l' : 'border-r'}`}>
                    {isRtl ? 'نسبة المرتجعات %' : 'Return Rate %'}
                  </th>
                  <th className="px-3 py-2.5 font-semibold text-gray-700 text-center whitespace-nowrap min-w-[90px]">
                    {isRtl ? 'الكفاءة %' : 'Efficiency %'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredData.map((row, idx) => {
                  const efficiency = row.efficiency || (100 - row.returnRate);
                  return (
                    <tr
                      key={row.id}
                      className={`hover:bg-blue-50/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                    >
                      {/* الخلايا الأساسية */}
                      <td className={`px-3 py-2.5 text-gray-800 text-center border-r border-gray-200 ${isRtl ? 'border-l' : 'border-r'}`}>
                        <div className="flex flex-col items-center">
                          <span className="font-mono font-medium text-xs bg-gray-100 px-2 py-1 rounded">
                            {row.code || '-'}
                          </span>
                        </div>
                      </td>
                      <td className={`px-3 py-2.5 text-gray-800 text-center border-r border-gray-200 ${isRtl ? 'border-l' : 'border-r'}`}>
                        <div className="flex flex-col items-center">
                          <span className="font-medium mb-0.5">{row.name}</span>
                          {row.department && (
                            <span className="text-[10px] text-gray-500 px-1.5 py-0.5 bg-gray-100 rounded mt-1">
                              {row.department}
                            </span>
                          )}
                        </div>
                      </td>
                      
                      {/* خلايا الفروع - 2 خلايا لكل فرع: طلبات + مرتجعات */}
                      {allBranches.map((branch, branchIndex) => {
                        const branchData = row.branchSummary?.[branch] || { orders: 0, returns: 0, net: 0 };
                        return (
                          <React.Fragment key={branch}>
                            <td className={`px-1 py-2 text-center border-r border-gray-200 ${branchIndex === allBranches.length - 1 && isRtl ? 'border-l-0' : ''} ${getQuantityCellColor(branchData.orders, 'orders')}`}>
                              <span className={`font-medium ${branchData.orders > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                                {formatNumber(branchData.orders, isRtl)}
                              </span>
                            </td>
                            <td className={`px-1 py-2 text-center border-r border-gray-200 ${branchIndex === allBranches.length - 1 && isRtl ? 'border-l-0' : ''} ${getQuantityCellColor(branchData.returns, 'returns')}`}>
                              <span className={`font-medium ${branchData.returns > 0 ? 'text-red-700' : 'text-gray-500'}`}>
                                {formatNumber(branchData.returns, isRtl)}
                              </span>
                            </td>
                          </React.Fragment>
                        );
                      })}
                      
                      {/* الخلايا الإجمالية */}
                      <td className={`px-3 py-2.5 text-center border-r border-gray-200 ${isRtl ? 'border-l' : 'border-r'} ${getQuantityCellColor(row.totalOrders, 'orders')}`}>
                        <span className="font-bold text-green-700">
                          {formatNumber(row.totalOrders, isRtl)}
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 text-center border-r border-gray-200 ${isRtl ? 'border-l' : 'border-r'} ${getQuantityCellColor(row.totalReturns, 'returns')}`}>
                        <span className="font-bold text-red-700">
                          {formatNumber(row.totalReturns, isRtl)}
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 text-center border-r border-gray-200 ${isRtl ? 'border-l' : 'border-r'}`}>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold border ${getNetColor(row.netQuantity)}`}>
                          {formatNumber(row.netQuantity, isRtl)}
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 text-center border-r border-gray-200 ${isRtl ? 'border-l' : 'border-r'}`}>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold border ${getReturnRateColor(row.returnRate)}`}>
                          {row.returnRate?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold border ${getEfficiencyColor(efficiency)}`}>
                          {efficiency.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
                
                {/* Totals Row */}
                <tr className="bg-blue-100 font-bold border-t-2 border-blue-200">
                  <td className={`px-3 py-2.5 text-gray-800 text-center border-r border-blue-200 ${isRtl ? 'border-l' : 'border-r'}`}>
                    {isRtl ? 'الإجمالي' : 'Total'}
                  </td>
                  <td className={`px-3 py-2.5 text-gray-800 text-center border-r border-blue-200 ${isRtl ? 'border-l' : 'border-r'}`}>
                    -
                  </td>
                  
                  {/* إجماليات الفروع */}
                  {allBranches.map((branch, branchIndex) => (
                    <React.Fragment key={branch}>
                      <td className={`px-1 py-2 text-gray-800 text-center border-r border-blue-200 ${branchIndex === allBranches.length - 1 && isRtl ? 'border-l-0' : ''} ${getQuantityCellColor(
                        filteredData.reduce((sum, row) => sum + (row.branchSummary?.[branch]?.orders || 0), 0), 'orders'
                      )}`}>
                        <span className="text-green-700">
                          {formatNumber(
                            filteredData.reduce((sum, row) => sum + (row.branchSummary?.[branch]?.orders || 0), 0),
                            isRtl
                          )}
                        </span>
                      </td>
                      <td className={`px-1 py-2 text-red-700 text-center border-r border-blue-200 ${branchIndex === allBranches.length - 1 && isRtl ? 'border-l-0' : ''} ${getQuantityCellColor(
                        filteredData.reduce((sum, row) => sum + (row.branchSummary?.[branch]?.returns || 0), 0), 'returns'
                      )}`}>
                        {formatNumber(
                          filteredData.reduce((sum, row) => sum + (row.branchSummary?.[branch]?.returns || 0), 0),
                          isRtl
                        )}
                      </td>
                    </React.Fragment>
                  ))}
                  
                  {/* الإجماليات الكلية */}
                  <td className={`px-3 py-2.5 text-green-700 text-center border-r border-blue-200 ${isRtl ? 'border-l' : 'border-r'} ${getQuantityCellColor(quickStats?.totalOrders || 0, 'orders')}`}>
                    {formatNumber(quickStats?.totalOrders || 0, isRtl)}
                  </td>
                  <td className={`px-3 py-2.5 text-red-700 text-center border-r border-blue-200 ${isRtl ? 'border-l' : 'border-r'} ${getQuantityCellColor(quickStats?.totalReturns || 0, 'returns')}`}>
                    {formatNumber(quickStats?.totalReturns || 0, isRtl)}
                  </td>
                  <td className={`px-3 py-2.5 text-center border-r border-blue-200 ${isRtl ? 'border-l' : 'border-r'}`}>
                    <span className={`px-2 py-1 rounded-full text-xs border ${getNetColor(quickStats?.totalNet || 0)}`}>
                      {formatNumber(quickStats?.totalNet || 0, isRtl)}
                    </span>
                  </td>
                  <td className={`px-3 py-2.5 text-center border-r border-blue-200 ${isRtl ? 'border-l' : 'border-r'}`}>
                    <span className={`px-2 py-1 rounded-full text-xs border ${getReturnRateColor(quickStats?.avgReturnRate || 0)}`}>
                      {formatPercentage(quickStats?.avgReturnRate || 0, isRtl)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs border ${getEfficiencyColor(quickStats?.avgEfficiency || 0)}`}>
                      {formatPercentage(quickStats?.avgEfficiency || 0, isRtl)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer Summary */}
      {!loading && filteredData.length > 0 && (
        <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
            <div className="text-xs text-gray-600">
              {isRtl 
                ? `عرض ${formatNumber(filteredData.length, isRtl)} منتج`
                : `Showing ${formatNumber(filteredData.length, isRtl)} products`
              }
            </div>
            <div className="flex flex-wrap md:flex-nowrap   items-center justify-center  gap-3 text-xs text-gray-700 font-medium">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>{isRtl ? 'الطلبات:' : 'Orders:'} <span className="text-green-600">{formatNumber(quickStats?.totalOrders || 0, isRtl)}</span></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span>{isRtl ? 'المرتجعات:' : 'Returns:'} <span className="text-red-600">{formatNumber(quickStats?.totalReturns || 0, isRtl)}</span></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span>{isRtl ? 'الصافي:' : 'Net:'} <span className="text-blue-600">{formatNumber(quickStats?.totalNet || 0, isRtl)}</span></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                <span>{isRtl ? 'نسبة المرتجعات:' : 'Return Rate:'} <span className="text-amber-600">{formatPercentage(quickStats?.avgReturnRate || 0, isRtl)}</span></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                <span>{isRtl ? 'الكفاءة:' : 'Efficiency:'} <span className="text-emerald-600">{formatPercentage(quickStats?.avgEfficiency || 0, isRtl)}</span></span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersVsReturnsReport;